import React, { Dispatch, useMemo, useState } from 'react'
import { actionAtom } from 'atoms/atoms'
import { useAtom, useSetAtom } from 'jotai'
import capitalize from 'lodash-es/capitalize'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  ActionList,
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  Icon,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { PathNames } from 'constants/General'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ContactInstrument, VerificationStatus } from 'types/contacts'
import { ActionIds } from 'types/types'
import { contactInstrumentAtom } from './atoms/contactAtoms'
import { ContactInstrumentDeleteModal } from './components/ContactInstrumentDeleteModal'

interface ContactInstrumentsListProps {
  contactInstruments: ContactInstrument[]
  setIsSheetOpen: Dispatch<React.SetStateAction<boolean>>
}

export const ContactInstrumentsList = ({
  contactInstruments,
  setIsSheetOpen,
}: ContactInstrumentsListProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { viewOnly } = useProfileContext() || {}
  const permissions = useMappedRoles()?.contacts
  const canEdit = permissions?.canEdit
  const [isDeleteInstrumentModalOpen, setIsDeleteInstrumentModalOpen] = useState(false)
  const setContactInstrument = useSetAtom(contactInstrumentAtom)
  const [action, setAction] = useAtom(actionAtom)

  const onEditClick = (contactInstrument: ContactInstrument) => {
    setContactInstrument(contactInstrument)
    setAction({
      ...action,
      actionId: ActionIds.INSTRUMENT,
      onClose: () => {
        setIsSheetOpen(false)
        setContactInstrument(null)
      },
      header: t('contacts.updateCryptoAccount'),
    })
    setIsSheetOpen(true)
  }

  const onDeleteClick = (contactInstrument: ContactInstrument) => {
    setContactInstrument(contactInstrument)
    setIsDeleteInstrumentModalOpen(true)
  }

  const onDeleteModalClose = () => {
    setIsDeleteInstrumentModalOpen(false)
    setContactInstrument(null)
  }

  const onVerifyClick = () => {
    navigate(`${PathNames.SETTINGS}${PathNames.MANAGE_PAYMENT_SUPPORT}`)
  }

  const actionItemListItems = useMemo(() => {
    return contactInstruments?.map(contactInstrument => {
      const { id, description, currency, address, network, verification } = contactInstrument || {}

      const isNotVerified =
        verification?.status === VerificationStatus.VERIFICATION_REQUESTED ||
        verification?.status === VerificationStatus.UNVERIFIED

      const headerHighlightLeft = isNotVerified && (
        <Highlight type="error">{t('contacts.notVerified')}</Highlight>
      )

      const actionButton = isNotVerified && (
        <Button onClick={onVerifyClick} variant="ghost" iconElement={<Icon name="ShieldCheck" />}>
          {t('verify')}
        </Button>
      )

      return {
        transparent: false,
        key: id,
        id: id,
        title: description,
        HeaderHighlightLeft: headerHighlightLeft,
        ActionButton: actionButton,
        Avatar: <Avatar type="currency" name={currency?.toLowerCase()} />,
        BodyPre: `${currency} (${capitalize(network)}) - ${ellipseMiddleText(address, 6, 6)}`,
        CustomAction: (
          <DropdownMenu
            DropdownMenuItems={[
              ...(canEdit && !viewOnly
                ? [
                    <DropdownMenuItem
                      onClick={() => onEditClick(contactInstrument)}
                      key={`action-edit-${id}`}
                      LeadingIcon={<Icon name="SimplePencilCustom" />}
                    >
                      {t('edit')}
                    </DropdownMenuItem>,
                  ]
                : []),
              ...(canEdit && !viewOnly
                ? [
                    <DropdownMenuItem
                      key={`action-delete-${id}`}
                      LeadingIcon={<Icon name="Trash" />}
                      onClick={() => onDeleteClick(contactInstrument)}
                    >
                      {t('delete')}
                    </DropdownMenuItem>,
                  ]
                : []),
            ]}
          >
            <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
          </DropdownMenu>
        ),
      }
    })
  }, [contactInstruments])

  return (
    <>
      <ActionList id="contact-instruments-list" items={actionItemListItems} />

      {isDeleteInstrumentModalOpen && (
        <ContactInstrumentDeleteModal onModalClose={onDeleteModalClose} />
      )}
    </>
  )
}
